import React, { useEffect, useState } from 'react';
import { X, Download, Smartphone, Share2, Plus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * iter302 — PWA install card.
 *
 * Behavior:
 *   • If the browser supports `beforeinstallprompt` (Chrome / Edge /
 *     Samsung Internet / desktop Chromium), wait for that event and
 *     show our SOFT "Install Wassla" card. Tapping "Install" calls the
 *     native `deferredPrompt.prompt()`.
 *   • If the event is NOT fired (iOS Safari, some PWA-enabled Android
 *     browsers that don't expose the event), we still show a soft
 *     instructional card that says "Open browser menu → Add to Home
 *     screen" — but only after the user has clearly engaged with the
 *     app (we wait 25s on the page and require a route change).
 *   • Already running as standalone? Render nothing.
 *   • Dismissed? Persisted via localStorage; never shown again unless
 *     the user manually clears storage or hits the "Show install
 *     prompt" button in Account → About (future work).
 */
const DISMISS_KEY = 'wassla:pwa-install-prompt-dismissed';
const FALLBACK_DELAY_MS = 25_000;

function detectInstructions() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'ios';
  }
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

export default function PWAInstallBanner() {
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(DISMISS_KEY) === '1') return;
    // Already installed?
    const mql = window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : false;
    if (mql || window.navigator.standalone === true) return;

    let fallbackTimer = null;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 4000);
      // If the native prompt fired, we don't need the fallback instructions.
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      setShowFallback(false);
    };
    const onInstalled = () => {
      localStorage.setItem(DISMISS_KEY, '1');
      setVisible(false);
      setShowFallback(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Fallback path — fires AFTER FALLBACK_DELAY_MS if the browser never
    // emitted beforeinstallprompt. We show a SOFT instructional card so
    // iOS users (and Firefox Android, etc.) still know how to install.
    fallbackTimer = setTimeout(() => {
      // If the native event already fired, deferredPrompt is set and we skip.
      if (deferredPrompt) return;
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      const stillStandalone = window.matchMedia
        ? window.matchMedia('(display-mode: standalone)').matches
        : false;
      if (stillStandalone) return;
      setShowFallback(true);
      setVisible(true);
    }, FALLBACK_DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
    // We intentionally have an empty dep array so the timer is set once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter322 — Hide the PWA banner while a fullscreen sheet/modal is
  // open (set via `document.body[data-modal-open]`). Without this,
  // the banner's `position:fixed; bottom:88px` overlaps the
  // `<LocationPickerSheet>` footer and intercepts taps on Apply.
  const [modalOpen, setModalOpen] = useState(
    () => typeof document !== 'undefined' && document.body.hasAttribute('data-modal-open')
  );
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      setModalOpen(document.body.hasAttribute('data-modal-open'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-modal-open'] });
    return () => observer.disconnect();
  }, []);

  if (!visible || modalOpen) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const accept = async () => {
    if (!deferredPrompt) {
      // Fallback mode — there's no prompt to invoke. Just close.
      dismiss();
      return;
    }
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        localStorage.setItem(DISMISS_KEY, '1');
      }
    } catch (_e) {
      /* Swallow — Chrome throws if prompt() is called twice. */
    } finally {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const platform = showFallback ? detectInstructions() : null;
  const instructions = (() => {
    if (!showFallback) return null;
    if (platform === 'ios') {
      return isArabic
        ? 'افتح زر المشاركة ثم اختر "إضافة إلى الشاشة الرئيسية".'
        : 'Tap the Share button, then choose "Add to Home Screen".';
    }
    if (platform === 'android') {
      return isArabic
        ? 'افتح قائمة المتصفح (⋮) ثم اختر "إضافة إلى الشاشة الرئيسية".'
        : 'Open the browser menu (⋮) and choose "Add to Home screen".';
    }
    return isArabic
      ? 'افتح قائمة المتصفح ثم اختر "تثبيت التطبيق" أو "إضافة إلى الشاشة الرئيسية".'
      : 'Open the browser menu and choose "Install app" or "Add to Home screen".';
  })();

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid={showFallback ? 'pwa-install-fallback' : 'pwa-install-banner'}
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 88, // sits above the bottom nav (~72px on mobile)
        zIndex: 1000,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
        padding: 14,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        maxWidth: 640,
        marginInline: 'auto',
      }}
    >
      <div
        style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid #fdba74',
        }}
      >
        <img
          src="/android-chrome-192x192.png?v=wassla-4"
          alt=""
          width={32}
          height={32}
          style={{ display: 'block', borderRadius: 8 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
          {isArabic ? 'ثبّت تطبيق وصلة' : 'Install Wassla'}
        </div>
        <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
          {showFallback
            ? instructions
            : (isArabic
              ? 'استخدم وصلة كتطبيق على جهازك — أسرع وأسهل في الوصول.'
              : 'Use Wassla as an app on your device — faster and easier to access.')}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {!showFallback ? (
            <button
              type="button"
              onClick={accept}
              data-testid="pwa-install-btn"
              style={{
                background: '#f97316', color: '#fff', border: 0,
                padding: '8px 14px', borderRadius: 10, fontWeight: 700, fontSize: 13,
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              }}
            >
              <Download size={14} /> {isArabic ? 'تثبيت' : 'Install'}
            </button>
          ) : (
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-fallback-ok"
              style={{
                background: '#f97316', color: '#fff', border: 0,
                padding: '8px 14px', borderRadius: 10, fontWeight: 700, fontSize: 13,
                display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              }}
            >
              {platform === 'ios' ? <Share2 size={14} /> : <Plus size={14} />}
              {isArabic ? 'فهمت' : 'Got it'}
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            style={{
              background: 'transparent', color: '#64748b', border: 0,
              padding: '8px 10px', borderRadius: 10, fontWeight: 600, fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {isArabic ? 'لاحقاً' : 'Not now'}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={isArabic ? 'إغلاق' : 'Close'}
        data-testid="pwa-install-close"
        style={{
          background: 'transparent', border: 0, color: '#94a3b8', cursor: 'pointer',
          padding: 4, alignSelf: 'flex-start',
        }}
      >
        <X size={18} />
      </button>
      {/* keep Smartphone import referenced to avoid linter warning */}
      <span style={{ display: 'none' }}><Smartphone size={1} /></span>
    </div>
  );
}
